import { Assets, Components, Services, Types, useWindowSize } from '@sendum/core'
import React, { useCallback, useEffect } from 'react'
import { shallowEqual, useDispatch, useSelector } from 'react-redux'
import { AnyAction } from 'redux'

import { Pallets } from './components/Pallets'
import { PalletsList } from './components/PalletsList'
import { TimelineContainer } from './components/TimelineContainer'
import s from './PalletSelector.module.scss'
import { loadAccount, loadPallets, refreshPallets, setPallet } from './store/PalletSelectorActions'
import { PalletSelectorRootState } from './store/PalletSelectorStore'

interface IProps {
    isMovement?: boolean
}

export const PalletSelector = ({ isMovement }: IProps): JSX.Element => {
    const dispatch = useDispatch()
    const { width } = useWindowSize()

    const PalletSelectorRef = React.useRef<HTMLDivElement>(null)
    const timer = React.useRef<ReturnType<typeof setInterval> | null>(null)

    const isInitialLoading = useSelector<PalletSelectorRootState, boolean | undefined>(
        (state: PalletSelectorRootState) => state.PalletSelector.isInitialLoading,
        shallowEqual
    )
    const isLoading = useSelector<PalletSelectorRootState, boolean | undefined>(
        (state: PalletSelectorRootState) => state.PalletSelector.isLoading,
        shallowEqual
    )
    const error = useSelector<PalletSelectorRootState, Error | undefined>(
        (state: PalletSelectorRootState) => state.PalletSelector.error,
        shallowEqual
    )
    const pallet = useSelector<PalletSelectorRootState, Types.Pallet | undefined>(
        (state: PalletSelectorRootState) => state.PalletSelector.pallet,
        shallowEqual
    )

    const handleOnClickBack = useCallback(
        (event: React.MouseEvent<HTMLDivElement>) => {
            event.preventDefault()
            event.stopPropagation()

            dispatch(setPallet(undefined))
        },
        [dispatch]
    )

    useEffect(() => {
        if (!isInitialLoading) {
            dispatch((loadAccount() as unknown) as AnyAction)
            dispatch((loadPallets(isMovement) as unknown) as AnyAction)

            Services.MQTT.ServiceMessages.subscribe(msg => {
                if (msg.action === 'force_update_pallets') {
                    dispatch((refreshPallets(isMovement) as unknown) as AnyAction)
                }
            })

            timer.current = setInterval(() => {
                dispatch((refreshPallets(isMovement) as unknown) as AnyAction)
            }, 60 * 1000)
        }

        return () => {
            Services.MQTT.ServiceMessages.unsubscribe()

            if (timer.current) {
                clearInterval(timer.current)
                timer.current = null
            }
        }
    }, [dispatch, isInitialLoading, isMovement])

    return (
        <div className={s.PalletSelector} ref={PalletSelectorRef}>
            {!isInitialLoading && (isLoading || isLoading === undefined) && (
                <Components.Loader text="Loading" className={s.loading} />
            )}
            <Components.ErrorComponent error={error} />

            {isInitialLoading === false && isLoading === false && !error && (
                <>
                    {!isMovement && (
                        <div className={s.PalletSelector__row}>
                            {(width >= 780 || !pallet) && ((width > 980 && !pallet) || (width > 1480 && !!pallet)) && (
                                <div className={s.PalletSelector__table}>
                                    <Pallets />
                                </div>
                            )}
                            {(width >= 780 || !pallet) && (width <= 980 || (width <= 1480 && !!pallet)) && (
                                <div className={s.PalletSelector__list}>
                                    <PalletsList />
                                </div>
                            )}
                            {!!pallet && (
                                <div className={s.PalletSelector__back} onClick={handleOnClickBack}>
                                    <img src={Assets.Close} alt="Close" />
                                </div>
                            )}
                            {!!pallet && <TimelineContainer pallet={pallet} />}
                        </div>
                    )}
                    {isMovement && (
                        <div className={s.PalletSelector__row}>
                            {width >= 980 && (
                                <div className={s.PalletSelector__table}>
                                    <Pallets isMovement />
                                </div>
                            )}
                            {width < 980 && (
                                <div className={s.PalletSelector__list}>
                                    <PalletsList isMovement />
                                </div>
                            )}
                        </div>
                    )}
                </>
            )}
        </div>
    )
}
